import React from "react";
import {
  Box,
  Button,
  Text,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider,
  Badge,
  Tag,
  TagLabel,
  Select as ChakraSelect,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import PropTypes from "prop-types";
import Map from "views/map/map";

const SectionHeader = ({ children }) => (
  <Heading
    as="h3"
    size="md"
    mb={2}
    borderBottom="1px solid"
    borderColor="gray.200"
    pb={1}
    color="blue.600"
  >
    {children}
  </Heading>
);

SectionHeader.propTypes = { children: PropTypes.node };

function Row({ label, children, spanFull = false }) {
  const content = (
    <Box>
      <Text fontSize="sm" fontWeight="bold" color="blue.600">
        {label}
      </Text>
      <Text fontSize="md" fontWeight="semibold" noOfLines={4}>
        {children ?? "-"}
      </Text>
    </Box>
  );
  return spanFull ? (
    <Box gridColumn={{ base: "auto", md: "1 / span 2" }}>{content}</Box>
  ) : content;
}

Row.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
  spanFull: PropTypes.bool,
};

const GeofenceTable = ({ t, geofence }) => {
  const points = Array.isArray(geofence?.coordinates?.[0])
    ? geofence.coordinates[0]
    : [];
  return (
    <Box>
      <SectionHeader>{t("Geofence Points")}</SectionHeader>
      {points.length ? (
        <Table size="sm" variant="simple" mb={2}>
          <Thead>
            <Tr>
              <Th>{t("Point")}</Th>
              <Th>{t("Latitude")}</Th>
              <Th>{t("Longitude")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {points.map((p, idx) => (
              <Tr key={idx}>
                <Td>{idx + 1}</Td>
                <Td>{Number(p[1]).toFixed(6)}</Td>
                <Td>{Number(p[0]).toFixed(6)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text fontSize="sm" color="gray.500">
          {t("No geofence points selected yet.")}
        </Text>
      )}
    </Box>
  );
};

GeofenceTable.propTypes = {
  t: PropTypes.func.isRequired,
  geofence: PropTypes.any,
};

export default function ViewBranchAccordionModal({
  t,
  viewingBranch,            // your payload object
  onClose,
  modalSize = "6xl",
  serviceCategories = [],   // optional: [{id, name}] for name resolution
}) {
  if (!viewingBranch) return null;

  const yesNo = (v) => (v ? t("Yes") : t("No"));

  // Resolve service category names (fallback to id)
  const resolvedServiceCats = (viewingBranch.service_category_ids || []).map((id) => {
    const found = serviceCategories.find((c) => String(c.id) === String(id));
    return found ? found.name : String(id);
  });

  const points = Array.isArray(viewingBranch?.geofence?.coordinates?.[0])
    ? viewingBranch.geofence.coordinates[0]
    : [];

  return (
    <Modal isOpen={true} onClose={onClose} size={modalSize}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="2xl" fontWeight="700" color="gray.800">
            {t("View Branch")}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody maxHeight="70vh" overflowY="auto">
          <Accordion allowToggle defaultIndex={[0]}>
            {/* Branch Details */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <SectionHeader>{t("Branch Details")}</SectionHeader>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                  <Row label={t("Name")}>{viewingBranch.name}</Row>
                  <Row label={t("Status")}>
                    <Badge colorScheme={viewingBranch.status === "open" ? "green" : "red"}>
                      {viewingBranch.status}
                    </Badge>
                  </Row>
                  <Row label={t("About")} spanFull>{viewingBranch.about}</Row>

                  <Row label={t("Manager ID")}>
                    {viewingBranch.branch_manager_id ?? t("Not assigned")}
                  </Row>
                  <Row label={t("Manager Name")}>{viewingBranch.branch_manager_name}</Row>
                  <Row label={t("Manager Email")}>{viewingBranch.branch_manager_email}</Row>
                </SimpleGrid>
              </AccordionPanel>
            </AccordionItem>

            {/* Contact & Location */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <SectionHeader>{t("Contact & Location")}</SectionHeader>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                  <Row label={t("Address Line 1")}>{viewingBranch.address_line1}</Row>
                  <Row label={t("Address Line 2")}>{viewingBranch.address_line2}</Row>
                  <Row label={t("City")}>{viewingBranch.city}</Row>
                  <Row label={t("State")}>{viewingBranch.state}</Row>
                  <Row label={t("Postal Code")}>{viewingBranch.postal_code}</Row>
                  <Row label={t("Country")}>{viewingBranch.country}</Row>
                  <Row label={t("Phone")}>{viewingBranch.phone_number}</Row>
                  <Row label={t("Email")}>{viewingBranch.email}</Row>
                  <Row label={t("Minimum Order Amount")}>
                    {viewingBranch.minimum_order_amount}
                  </Row>
                  <Row label={t("Latitude")}>
                    {viewingBranch.latitude != null
                      ? Number(viewingBranch.latitude).toFixed(6)
                      : "-"}
                  </Row>
                  <Row label={t("Longitude")}>
                    {viewingBranch.longitude != null
                      ? Number(viewingBranch.longitude).toFixed(6)
                      : "-"}
                  </Row>
                </SimpleGrid>

                <Divider my={4} />

                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} alignItems="start">
                  <Box>
                    <SectionHeader>{t("Map (read-only)")}</SectionHeader>
                    <Map
                      // Read-only: we just pass initial shapes; no handlers needed
                      initialPolygon={points.map((p) => ({
                        lat: Number(p[1]),
                        lng: Number(p[0]),
                      }))}
                      initialMarker={
                        viewingBranch?.latitude && viewingBranch?.longitude
                          ? {
                              lat: Number(viewingBranch.latitude),
                              lng: Number(viewingBranch.longitude),
                            }
                          : null
                      }
                    />
                  </Box>
                  <Box>
                    <GeofenceTable t={t} geofence={viewingBranch.geofence} />
                    <Box mt={4}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={1}>
                        {t("Geofence")}
                      </Text>
                      <ChakraSelect variant="filled" bg="gray.50" isDisabled>
                        {points.length > 0 ? (
                          points.map((p, idx) => (
                            <option key={idx} value={idx}>
                              {t("Point")} {idx + 1}: ({Number(p[1]).toFixed(6)}, {Number(p[0]).toFixed(6)})
                            </option>
                          ))
                        ) : (
                          <option>{t("No geofence points available")}</option>
                        )}
                      </ChakraSelect>
                    </Box>
                  </Box>
                </SimpleGrid>
              </AccordionPanel>
            </AccordionItem>

            {/* Delivery & Pricing */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <SectionHeader>{t("Delivery & Pricing")}</SectionHeader>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                  <Row label={t("UC Wallet")}>
                    {yesNo(Number(viewingBranch.uc_wallet) === 1)}
                  </Row>
                  <Row label={t("UC Delivery")}>
                    {yesNo(Number(viewingBranch.uc_delivery) === 1)}
                  </Row>
                  <Row label={t("Free Delivery")}>
                    {yesNo(!!viewingBranch.free_delivery)}
                  </Row>
                  <Row label={t("Delivery Fee")}>{viewingBranch.delivery_fee}</Row>
                  <Row label={t("Clean at Location Fee")}>{viewingBranch.clean_at_location}</Row>
                </SimpleGrid>
              </AccordionPanel>
            </AccordionItem>

            {/* Settings */}
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <SectionHeader>{t("Settings")}</SectionHeader>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                  <Row label={t("Completion Date (Hours)")}>
                    {viewingBranch.completion_date}
                  </Row>

                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                      {t("Service Categories")}
                    </Text>
                    <Box mt={1} display="flex" gap={2} flexWrap="wrap">
                      {(resolvedServiceCats.length ? resolvedServiceCats : ["-"]).map((name, i) => (
                        <Tag key={i} size="sm" colorScheme="cyan">
                          <TagLabel>{name}</TagLabel>
                        </Tag>
                      ))}
                    </Box>
                  </Box>
                </SimpleGrid>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          <Divider my={4} />

          {/* Meta */}
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            <Row label={t("Active")}>{yesNo(!!viewingBranch.is_active)}</Row>
            <Row label={t("Created At")}>{viewingBranch.created_at}</Row>
            <Row label={t("Updated At")}>{viewingBranch.updated_at}</Row>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter justifyContent="center" py={4}>
          <Button colorScheme="blue" onClick={onClose} px={8}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

ViewBranchAccordionModal.propTypes = {
  t: PropTypes.func.isRequired,
  viewingBranch: PropTypes.object,  // your API data object
  onClose: PropTypes.func.isRequired,
  modalSize: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  serviceCategories: PropTypes.array, // optional: [{id, name}]
};
